'use client';

import React, { FC } from 'react';
import ImageSlider from '@/hotdesk-ui/components/image-slider/ImageSlider';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Heart, X } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { addFavorite, removeFavorite } from '@/hotdesk-ui/store/slices/favorites.slice';
import { showFavoriteToast } from '@/hotdesk-ui/helpers/favorite-toast';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { AuthPopupService } from '@/hotdesk-ui/services/authPopup.service';
import { getListingProviderCode } from '@/hotdesk-ui/utils/provider';
import { iwgBookableUnit, iwgBookableUnits, shouldRequestIwgLongTermQuote, withBookableFrequency } from '@/hotdesk-ui/helpers/explore-location-params';
import { useLongTermQuote } from '@/hotdesk-ui/components/long-term-quote/LongTermQuoteContext';
import { parseRatingFromMetaData } from '@/hotdesk-ui/helpers/rating.utils';
import RatingBadge from '@/hotdesk-ui/components/shared/RatingBadge';
import Link from 'next/link';
import styles from './CardOnMap.module.scss';
export interface ListingTileProps {
  item: MyListing;
  moveToRoute?: () => void;
  distance?: number;
  getdistance?: boolean;
  dir_rtl?: boolean;
  listingRating?: number;
  rating_feature?: boolean;
  price?: string;
  baseCurrencyName?: string;
  languageCode?: string;
  price_unit?: string;
  url: string;
  target?: string;
  rel?: string;
  type?: string;
  /** Renders the close (X) control on the image when provided. */
  onClose?: () => void;
}
const CardOnMap: FC<ListingTileProps> = ({
  item,
  price = '',
  baseCurrencyName = '',
  price_unit = '',
  moveToRoute,
  url,
  target = '_self',
  rel = '',
  type,
  onClose
}) => {
  const t = useTranslations('landing-v4.card');
  const tExplore = useTranslations('explore');
  const tFavorites = useTranslations('favorites');
  const searchParams = useSearchParams();
  const longTermQuote = useLongTermQuote();
  const dispatch = useAppDispatch();
  const favoritesMap = useAppSelector((state: RootState) => state.favorites.items);
  const user = useAppSelector((state: RootState) => state.auth.user);
  const isWorkspace = type === 'workspace';
  const isIwgLongTermQuote = shouldRequestIwgLongTermQuote(item, searchParams.get('frequency'), !!getListingProviderCode(item));
  // Quote cards with a day/hour rate also offer booking that unit: the card
  // opens details on it and Request Quote becomes its own half-button.
  // Without one the whole card stays a quote request.
  const canBookUnit = isIwgLongTermQuote && !!iwgBookableUnit(item.rules?.price);
  const quoteOnlyCard = isIwgLongTermQuote && !canBookUnit;
  const bookableUnitsLabel = iwgBookableUnits(item.rules?.price).map(unit => t(unit === 'hour' ? 'hourly' : 'daily')).join('/');
  const cardUrl = canBookUnit ? withBookableFrequency(url, item.rules?.price) : url;
  const openLongTermQuote = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!longTermQuote) return;
    const workspaceTitle = item.workspace?.title || '';
    const listingTitle = item.title || '';
    longTermQuote.requestQuote({
      spaceName: workspaceTitle,
      listingName: listingTitle,
      city: item.workspace?.workspaceCity?.name || item.city?.name || '',
      listingId: item.id,
      workspaceId: item.workspace?.id,
      listingTypeId: item.type ?? null,
      source: 'IWG'
    });
  };
  const title = isWorkspace ? item.workspace?.title || item.title : item.title || item.workspace?.title;
  const images = isWorkspace ? item?.workspace?.rules?.images || {} : item?.rules?.images || {};

  // Area + city, de-duplicated (e.g. "Madinaty, New Cairo"); collapses to a
  // single label when they coincide.
  const workspaceCityName = item.workspace?.workspaceCity?.name;
  const cityName = item.city?.name;
  const location = Array.from(new Set([workspaceCityName, cityName].filter(Boolean))).join(', ');

  // Recommended providers (IWG / Regus) get the "Premium" badge.
  const isRecommended = !!getListingProviderCode(item);
  const ratingParsed = parseRatingFromMetaData(isWorkspace ? item.workspace?.metaData || item.metaData : item.metaData);
  const rating = ratingParsed.displayRating ? Number(ratingParsed.displayRating) : 0;
  const reviewCount = ratingParsed.count;

  // Favourites are listing-scoped in the backend, so workspace cards have
  // nothing to toggle.
  const listingId = item.id as number;
  const favoriteId = favoritesMap[listingId] ?? null;
  const isFavorite = favoriteId !== null;
  const handleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      new AuthPopupService().getPopup();
      return;
    }
    try {
      if (isFavorite && favoriteId) {
        await dispatch(removeFavorite({
          listingId,
          favoriteId
        })).unwrap();
        firebaseAnalytics('listing_unfavorited', {
          listingId
        });
        showFavoriteToast({
          message: tFavorites('removedToast'),
          viewAll: tFavorites('viewAll')
        });
      } else {
        await dispatch(addFavorite({
          userId: user.id!,
          listingId
        })).unwrap();
        firebaseAnalytics('listing_favorited', {
          listingId
        });
        showFavoriteToast({
          message: tFavorites('addedToast'),
          viewAll: tFavorites('viewAll')
        });
      }
    } catch (error) {
      console.error('Error updating favorite:', error);
    }
  };
  return <div className={styles.card} id="listing-card" data-sentry-component="CardOnMap" data-sentry-source-file="CardOnMap.tsx">
            <div className={styles.imageWrap}>
                {item && <ImageSlider title={item?.workspace?.title || ''} images={images} moveToRoute={quoteOnlyCard ? () => openLongTermQuote() : moveToRoute} url={quoteOnlyCard ? '#' : cardUrl} width={'406px'} height={'240px'} mapCard target={quoteOnlyCard ? '_self' : target} rel={quoteOnlyCard ? '' : rel} />}

                {/* Top-left stack: Premium (recommended) above the rating pill,
                    mirroring the explore card. */}
                <div className={styles.overlaysLeft}>
                    {isRecommended && <span className={styles.premiumBadge}>
                            <Image src="/assets/icons/white-check.svg" alt="" width={12} height={12} className={styles.premiumIcon} />
                            {t('premium')}
                        </span>}
                    {!!rating && <RatingBadge rating={rating} reviewCount={reviewCount} />}
                </div>

                {!isWorkspace && <button type="button" className={`${styles.favoriteBtn} ${onClose ? styles.favoriteBtnShifted : ''}`} onClick={handleFavorite} aria-label="Add to favorites">
                        <Heart size={16} color="#fff" fill={isFavorite ? '#000' : 'rgba(0, 0, 0, 0.25)'} />
                    </button>}

                {onClose && <button type="button" className={styles.closeBtn} onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }} aria-label={tExplore('close')}>
                        <X size={16} strokeWidth={2.5} />
                    </button>}
            </div>

            <Link href={quoteOnlyCard ? '#' : cardUrl} onClick={e => {
      if (quoteOnlyCard) {
        openLongTermQuote(e);
        return;
      }
      moveToRoute?.();
    }} target={quoteOnlyCard ? '_self' : target} rel={quoteOnlyCard ? '' : rel} className={styles.body} data-sentry-element="Link" data-sentry-source-file="CardOnMap.tsx">
                <p className={styles.title} title={title} dir="auto">
                    {title}
                </p>
                {!!location && <p className={styles.location} title={location}>
                        {location}
                    </p>}
                {isIwgLongTermQuote ? <div className={styles.quoteSplit}>
                        <button type="button" className={styles.quoteBtn} onClick={openLongTermQuote}>
                            <span className={styles.quoteBtnSub}>
                                {t('longTermTerms')}
                            </span>
                            <span className={styles.quoteBtnTitle}>
                                {t('requestQuote')}
                            </span>
                        </button>
                        {/* Not a button: the click falls through to the card
                            link, which already targets the unit. */}
                        {canBookUnit && <span className={`${styles.quoteBtn} ${styles.bookBtn}`}>
                                <span className={styles.quoteBtnSub}>
                                    {bookableUnitsLabel}
                                </span>
                                <span className={styles.quoteBtnTitle}>
                                    {t('instantBooking')}
                                </span>
                            </span>}
                    </div> : !!price && !!baseCurrencyName && !!price_unit && <div className={styles.priceRow}>
                            <span className={styles.priceValue}>
                                {baseCurrencyName} {price}
                            </span>
                            <span className={styles.priceUnit}>
                                /{t(price_unit)}
                            </span>
                        </div>}
            </Link>
        </div>;
};
export default CardOnMap;